import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Download, ExternalLink, Shield, Crown, Loader2 } from "lucide-react";

const ASSET_DOWNLOADS = [
  { id: "monogram-svg", label: "Monogram · SVG (Vector)", desc: "Brass-on-transparent. Scales infinitely. Use for web, signage, embroidery.", href: "/brand-kit/wealthfirst-monogram-brass.svg" },
  { id: "lockup-affiliate", label: "Affiliate Lockup · SVG", desc: "\"A WealthFirst Affiliate\" lockup for franchise partners.", href: "/brand-kit/wealthfirst-affiliate-lockup.svg" },
  { id: "guidelines-html", label: "Brand Guidelines · HTML / PDF", desc: "44-page identity standards — open and ⌘P to save as PDF.", href: "/brand-kit/wealthfirst-brand-guidelines.html" },
  { id: "tokens-css", label: "Color & Type Tokens · CSS", desc: "CSS custom properties for engineering teams.", href: "/brand-kit/wealthfirst-tokens.css" },
  { id: "tokens-json", label: "Design Tokens · JSON", desc: "Machine-readable tokens for Figma / Style Dictionary.", href: "/brand-kit/wealthfirst-tokens.json" },
  { id: "logo-html", label: "Brand Kit Reference · /logo.html", desc: "Public-facing brand standards page — share with vendors.", href: "/logo.html" },
];

export default function LicenseDashboard() {
  const navigate = useNavigate();
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/license/me")
      .then((r) => {
        setLicense(r.data);
        setLoading(false);
        if (!r.data?.is_authenticated) navigate("/login?next=/license/dashboard");
      })
      .catch(() => {
        setLoading(false);
        navigate("/login?next=/license/dashboard");
      });
  }, [navigate]);

  if (loading) {
    return (
      <div className="max-w-[760px] mx-auto px-6 lg:px-10 pt-32 pb-24 text-center">
        <Loader2 size={32} className="animate-spin mx-auto" style={{ color: "var(--brass)" }} />
      </div>
    );
  }

  if (!license?.license_active) {
    return (
      <div className="max-w-[760px] mx-auto px-6 lg:px-10 pt-32 pb-24 text-center" data-testid="license-no-active">
        <p className="eyebrow mb-4">No Active License</p>
        <h1 className="font-display text-5xl md:text-6xl leading-tight mb-6">
          Your account doesn't hold a license yet.
        </h1>
        <p className="lede mb-10">
          The WealthFirst Brand Kit is reserved for annual licensees. Apply to gain access to the full asset suite.
        </p>
        <Link
          to="/license"
          className="inline-flex items-center gap-3 px-8 py-4 text-xs uppercase tracking-[0.18em]"
          style={{ background: "var(--brass)", color: "var(--bg)" }}
          data-testid="apply-for-license-cta"
        >
          Apply for License →
        </Link>
      </div>
    );
  }

  const daysToExpiry = license.expires_at
    ? Math.ceil((new Date(license.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="max-w-[1180px] mx-auto px-6 lg:px-10 pt-32 pb-24" data-testid="license-dashboard">
      {/* Hero */}
      <div className="mb-12">
        <p className="eyebrow mb-4">Licensee Dashboard</p>
        <h1 className="font-display text-5xl md:text-6xl leading-tight mb-6">
          Welcome back, <em style={{ color: "var(--brass)" }}>{license.name || license.email}</em>.
        </h1>
      </div>

      {/* License Status Card */}
      <div className="mb-16 p-8" style={{ border: "1px solid var(--brass)", background: "var(--surface)" }} data-testid="license-status-card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="eyebrow mb-2">Status</p>
            <p className="font-display text-2xl" style={{ color: "var(--brass)" }}>Active</p>
          </div>
          <div>
            <p className="eyebrow mb-2">Expires</p>
            <p className="font-display text-2xl">{license.expires_at?.slice(0, 10) || "—"}</p>
            {daysToExpiry !== null && (
              <p className="text-xs mt-1" style={{ color: daysToExpiry < 30 ? "#E08585" : "var(--ink-muted)" }}>
                {daysToExpiry > 0 ? `${daysToExpiry} days remaining` : "Expired — renew now"}
              </p>
            )}
          </div>
          <div>
            <p className="eyebrow mb-2">License ID</p>
            <p className="font-display text-2xl" style={{ color: "var(--ink-muted)" }}>
              {license.license_id ? license.license_id.slice(0, 12) + "…" : "—"}
            </p>
          </div>
        </div>
        {daysToExpiry !== null && daysToExpiry < 30 && (
          <div className="mt-6 pt-6 border-t flex items-center justify-between flex-wrap gap-4" style={{ borderColor: "var(--hairline)" }}>
            <p className="text-sm" style={{ color: daysToExpiry < 0 ? "#E08585" : "var(--ink-muted)" }}>
              {daysToExpiry < 0
                ? "Your license has expired. Renew now to restore download access."
                : `Renewal window open. Renew now to lock in the next 12 months.`}
            </p>
            <Link to="/license" className="btn-brass" data-testid="renew-license-button">
              Renew License →
            </Link>
          </div>
        )}
      </div>

      {/* Downloadable Assets */}
      <section className="mb-16">
        <p className="eyebrow mb-4">Brand Kit · Downloadable Assets</p>
        <h2 className="font-display text-4xl mb-8 leading-tight">
          The <em style={{ color: "var(--brass)" }}>complete</em> kit.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ASSET_DOWNLOADS.map((a) => (
            <a
              key={a.id}
              href={a.href}
              download
              target="_blank"
              rel="noreferrer"
              className="flex items-start justify-between p-6 transition-colors hover:bg-[var(--surface)]"
              style={{ border: "1px solid var(--hairline)" }}
              data-testid={`download-${a.id}`}
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-xl mb-1">{a.label}</h3>
                <p className="text-xs" style={{ color: "var(--ink-muted)" }}>{a.desc}</p>
              </div>
              <Download size={20} className="ml-4 shrink-0" style={{ color: "var(--brass)" }} />
            </a>
          ))}
        </div>
        <p className="text-xs mt-6" style={{ color: "var(--ink-faint)" }}>
          All licensed assets are immediately downloadable. The Brand Guidelines HTML file is print-optimized — open it and press <kbd style={{ background: "var(--surface)", border: "1px solid var(--hairline-strong)", padding: "2px 6px", fontFamily: "monospace", fontSize: "11px" }}>⌘P</kbd> / <kbd style={{ background: "var(--surface)", border: "1px solid var(--hairline-strong)", padding: "2px 6px", fontFamily: "monospace", fontSize: "11px" }}>Ctrl+P</kbd> to save as a PDF. For custom assets (lower-thirds, broadcast slates, signage), email <a href={`mailto:support@wealthfirstinc.tv?subject=Custom+Brand+Asset+Request+-+${license.email}`} className="brass-link">support@wealthfirstinc.tv</a>.
        </p>
      </section>

      {/* Quick links */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <a href="/logo.html" target="_blank" rel="noreferrer" className="p-6 hover:bg-[var(--surface)] transition-colors" style={{ border: "1px solid var(--hairline)" }} data-testid="quick-logo-html">
          <ExternalLink size={20} style={{ color: "var(--brass)" }} className="mb-3" />
          <h3 className="font-display text-xl mb-1">Brand Kit Reference</h3>
          <p className="text-xs" style={{ color: "var(--ink-muted)" }}>Public-facing brand standards at /logo.html — share with vendors.</p>
        </a>
        <Link to="/contact" className="p-6 hover:bg-[var(--surface)] transition-colors block" style={{ border: "1px solid var(--hairline)" }} data-testid="quick-concierge">
          <Crown size={20} style={{ color: "var(--brass)" }} className="mb-3" />
          <h3 className="font-display text-xl mb-1">Concierge Review</h3>
          <p className="text-xs" style={{ color: "var(--ink-muted)" }}>Request a brand-compliance check of your campaigns.</p>
        </Link>
        <a href="mailto:support@wealthfirstinc.tv?subject=Brand%20License%20-%20Question" className="p-6 hover:bg-[var(--surface)] transition-colors block" style={{ border: "1px solid var(--hairline)" }} data-testid="quick-support">
          <Shield size={20} style={{ color: "var(--brass)" }} className="mb-3" />
          <h3 className="font-display text-xl mb-1">Licensing Support</h3>
          <p className="text-xs" style={{ color: "var(--ink-muted)" }}>Email support@wealthfirstinc.tv for License Agreement questions.</p>
        </a>
      </section>
    </div>
  );
}
